import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import { grey } from "@mui/material/colors";
import type { PaperProps } from "@mui/material/Paper";
import type { SxProps, Theme } from "@mui/material/styles";
import type { Options } from "@popperjs/core";
import type { FC, ReactNode } from "react";
import { useEffect } from "react";

/** Default chrome for the suggestion detail paper (explainability, ARAS, etc.). */
export const suggestionDetailPopoverPaperSx: SxProps<Theme> = {
  borderRadius: "0.5rem",
  maxWidth: "26rem",
  minWidth: "17.5rem",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.12)",
  border: `1px solid ${grey[200]}`,
  maxHeight: "min(70vh, 28rem)",
  overflowY: "auto",
  pointerEvents: "auto",
};

/** Max width of ARAS score popover paper (design spec). */
export const ARAS_POPOVER_PAPER_MAX_WIDTH = "268px";

export const arasSuggestionDetailPopoverShellSx: SxProps<Theme> = {
  maxWidth: ARAS_POPOVER_PAPER_MAX_WIDTH,
  minWidth: 0,
};

const popperModifiers: NonNullable<Options["modifiers"]> = [
  {
    name: "offset",
    enabled: true,
    options: {
      offset: [0, 8],
    },
  },
  {
    name: "flip",
    enabled: true,
    options: {
      padding: 8,
      fallbackPlacements: [
        "top-start",
        "top-end",
        "right-start",
        "right-end",
        "left-start",
        "left-end",
        "bottom-end",
      ],
    },
  },
  {
    name: "preventOverflow",
    enabled: true,
    options: {
      boundary: "viewport",
      padding: 8,
      altAxis: true,
    },
  },
];

export interface SuggestionDetailPopoverShellProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  children: ReactNode;
  /** Merged into Paper `sx` after defaults (e.g. {@link arasSuggestionDetailPopoverShellSx}). */
  sx?: SxProps<Theme>;
  /** Hover bridge: keep the popover open while the pointer moves from the anchor onto the paper. */
  paperProps?: Partial<PaperProps>;
}

/**
 * Popper-based shell: prefers below the anchor, then above, then to the side when vertical space is tight.
 * Root uses `pointer-events: none` so anchors stay hoverable; Paper uses `auto`.
 */
const SuggestionDetailPopoverShell: FC<SuggestionDetailPopoverShellProps> = ({
  open,
  anchorEl,
  onClose,
  children,
  sx: sxProp,
  paperProps,
}) => {
  const { sx: paperSx, ...paperRest } = paperProps ?? {};

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const resolvedOpen = open && anchorEl != null;

  if (!resolvedOpen) {
    return null;
  }

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement="bottom-start"
      transition
      modifiers={popperModifiers}
      popperOptions={{
        strategy: "fixed",
      }}
      sx={{
        zIndex: (theme) => theme.zIndex.modal,
        pointerEvents: "none",
      }}
    >
      {({ TransitionProps }) => (
        <Grow {...TransitionProps} timeout={220}>
          <Paper
            elevation={2}
            {...paperRest}
            sx={[
              suggestionDetailPopoverPaperSx,
              ...(sxProp != null
                ? Array.isArray(sxProp)
                  ? sxProp
                  : [sxProp]
                : []),
              { overflow: "hidden" },
              ...(paperSx != null
                ? Array.isArray(paperSx)
                  ? paperSx
                  : [paperSx]
                : []),
            ]}
          >
            {children}
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};

export default SuggestionDetailPopoverShell;
